import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'
import type { SharingFmt } from '@/i18n/messages/en/sharing.ts'

/**
 * `sharing` em português do Brasil. Declarado, nunca inferido, então uma chave que falte
 * quebra o `tsc` (PRD-27 LOC-011).
 *
 * *solicitação de compartilhamento*, nunca *partilha* nem *pedido de partilha* (ADR-0018).
 * "Aguardando revisão" e "Todas as pessoas que entram" são frases fixadas e vêm da lista
 * ratificada.
 *
 * Tudo aqui fala de UMA solicitação, que é feminina — por isso "Aprovada", "Recusada",
 * "Retirada", "Encerrada". É o caso fácil de concordância neste PRD: a entidade é uma só e
 * está sempre em foco (D-21).
 */

const pick = (fmt: SharingFmt, count: number, forms: { one: string; other: string }): string =>
  pluralCategory(fmt.locale, count) === 'one' ? forms.one : forms.other

const counted = (fmt: SharingFmt, count: number, forms: { one: string; other: string }): string =>
  `${fmt.formatCount(count)} ${pick(fmt, count, forms)}`

const people = (fmt: SharingFmt, count: number): string => counted(fmt, count, { one: 'pessoa', other: 'pessoas' })

export const sharing: Messages['sharing'] = {
  view: 'Solicitações de compartilhamento',
  waiting: (fmt, count) => `${fmt.formatCount(count)} aguardando`,
  lead: 'Agentes e conhecimento que alguém quer compartilhar além dos grupos que gerencia. Nada aqui está no ar até você aprovar.',
  empty: 'Nada aguardando revisão',
  emptyBody:
    'Quando alguém compartilha um agente ou uma base de conhecimento com todo mundo, ou com um grupo que não gerencia, a solicitação espera aqui. Compartilhar com grupos que a pessoa gerencia entra no ar sem passar por você.',
  loading: 'Carregando as solicitações de compartilhamento…',
  failed: 'Não foi possível carregar as solicitações de compartilhamento.',
  failedBody: 'É um problema de leitura da fila, não uma fila sem nada dentro. Tente de novo em instantes.',
  showHistory: 'Mostrar as solicitações já decididas',
  hideHistory: 'Mostrar só o que está aguardando',
  kinds: { agent: 'Agente', knowledge_base: 'Base de conhecimento' },
  // "X pediu" em vez de "Pedido por X": o particípio concordaria com quem pediu, e esta linha
  // nunca sabe o gênero dessa pessoa.
  asked: (name, when) => `${name ?? 'Alguém que já saiu'} pediu · ${when}`,
  wantsToShare: 'Quer compartilhar com',
  everyone: 'Todas as pessoas que entram',
  added: 'adicionado',
  now: 'Quem pode usar agora',
  nowPrivate: 'Só quem mantém',
  nowEveryone: 'Todas as pessoas que entram',
  reach: (fmt, now, ifApproved) => `Alcança ${people(fmt, now)} agora; ${people(fmt, ifApproved)} se você aprovar.`,
  why: {
    everyone: 'Compartilhar com todo mundo sempre precisa da revisão de um administrador.',
    unmanaged_groups: (fmt, requester, groups) => `${requester ?? 'Quem pediu'} não gerencia ${fmt.formatList(groups)}.`
  },
  personWhoAsked: 'quem pediu',
  what: 'O que essas pessoas receberiam',
  version: (wouldPublish, requested, live) =>
    wouldPublish === requested
      ? `A versão ${wouldPublish} entraria no ar${live === null ? '' : live === wouldPublish ? ' — ela já está no ar para quem pode usar agora' : ` (a versão ${live} está no ar agora)`}.`
      : `A versão ${wouldPublish} entraria no ar — a pessoa perguntou sobre a versão ${requested}, e uma mais nova foi publicada desde então.`,
  changedSince: (fmt, fields) => `Mudou desde o pedido: ${fmt.formatList(fields)}.`,
  fieldNames: {
    instructions: 'instruções',
    knowledge: 'conhecimento',
    capabilities: 'o que ele pode fazer',
    grounded: 'ancoragem',
    memory: 'memória',
    displayName: 'nome',
    description: 'descrição',
    tagline: 'resumo',
    starters: 'perguntas iniciais',
    category: 'categoria',
    icon: 'ícone',
    accent: 'cor',
    knowledgeMode: 'conhecimento'
  },
  fieldUnknown: 'outra coisa',
  knowledge: 'O conhecimento de onde ele responde',
  knowledgeAll: 'Tudo o que cada pessoa pode usar — nenhuma base está anexada.',
  abilities: 'O que ele pode fazer',
  noAbilities: 'Ele só responde; não age.',
  reads: 'lê',
  acts: 'age',
  grounded: 'Responde só a partir do conhecimento dele.',
  notGrounded: 'Pode responder com conhecimento geral, além do próprio.',
  memoryLearns: 'Lembra o que as pessoas pedem para ele lembrar.',
  memoryDoesNot: 'Não aprende nada novo sobre as pessoas.',
  showInstructions: 'Mostrar as instruções dele',
  hideInstructions: 'Ocultar as instruções dele',
  base: (fmt, documents, folders) =>
    `${counted(fmt, documents, { one: 'documento', other: 'documentos' })}${
      folders === 0 ? '' : ` · ${counted(fmt, folders, { one: 'pasta conectada', other: 'pastas conectadas' })}`
    }`,
  sampleTitles: 'Parte do que ela tem',
  coversLater: {
    agent: 'Depois da aprovação, as versões seguintes chegam a essas pessoas sem outra revisão.',
    knowledge_base: 'Depois da aprovação, tudo o que for adicionado a ela também chega a essas pessoas.'
  },
  acknowledged: 'O que a pessoa confirmou ao pedir',
  note: 'Nota para quem pediu',
  noteHint: 'Obrigatório para recusar — diga por quê, para essa pessoa poder agir.',
  approve: 'Aprovar e compartilhar',
  decline: 'Recusar',
  deciding: 'Salvando…',
  stale: 'Esta solicitação mudou enquanto você lia. Ela foi recarregada — leia de novo antes de decidir.',
  alreadyDecided: 'Esta solicitação foi decidida agora há pouco. A lista foi recarregada.',
  status: {
    pending: 'Aguardando revisão',
    approved: 'Aprovada',
    declined: 'Recusada',
    withdrawn: 'Retirada',
    // O badge tem uma coluna estreita e o português já corre 15–25% mais longo: a forma curta
    // diz a mesma coisa, porque o sujeito ("a solicitação") está no próprio cartão.
    superseded: 'Substituída por uma mais nova',
    closed: 'Encerrada'
  },
  closedReason: {
    group_deleted: 'um grupo que ela citava foi excluído',
    requester_lost_authority: 'quem pediu não mantém mais isso',
    item_unavailable: 'não dá mais para compartilhar do jeito que foi pedido',
    migrated: 'os grupos que ela citava não existem mais'
  },
  decidedBy: (name, when) => `${name ?? 'Alguém'} · ${when}`,
  notWaiting: (request) =>
    request.status === 'superseded'
      ? `A solicitação de “${request.resourceName}” foi substituída por uma mais nova — ela está na lista abaixo se ainda estiver aguardando.`
      : request.status === 'withdrawn'
        ? `A solicitação de “${request.resourceName}” foi retirada, então não há nada para decidir.`
        : request.status === 'pending'
          ? `A solicitação de “${request.resourceName}” está aguardando.`
          : `A solicitação de “${request.resourceName}” já foi ${request.status === 'approved' ? 'aprovada' : request.status === 'declined' ? 'recusada' : 'encerrada'}${request.decidedBy ? ` por ${request.decidedBy.displayName}` : ''}.`,
  open: {
    agent: 'Abrir o agente',
    knowledge_base: 'Abrir a base de conhecimento'
  },

  outcome: {
    approvedEveryone: (name) => `Aprovada — todas as pessoas que entram já podem usar “${name}”.`,
    // O verbo concorda com quantos grupos a lista nomeia; o inglês não precisa disso.
    approvedGroups: (fmt, groups, name) =>
      `Aprovada — ${fmt.formatList(groups)} já ${
        pluralCategory(fmt.locale, groups.length) === 'one' ? 'pode' : 'podem'
      } usar “${name}”.`,
    // "recebe um aviso" em vez de "é avisado": quem pediu pode ser qualquer pessoa, e o
    // particípio pediria um gênero que esta frase não tem. *nota* é a palavra do catálogo
    // inteiro para um recado escrito (`feedback`, `approvals`, `knowledge`, `maker`).
    declined: (who) => `Recusada — ${who} recebe um aviso, com a sua nota.`,
    closed: (name, why) => `Nada foi compartilhado: a solicitação de “${name}” foi encerrada porque ${why}.`,
    closedUnknown: 'não deu para compartilhar do jeito que foi pedido',
    other: (status, name) => `${status} — “${name}”.`
  }
}
